import { Observable } from 'rxjs';
import {
  hideRequestFallbackPreloader,
  pingRequestBusy$,
  shouldWaitForPingRequest,
  showRequestFallbackPreloader,
} from './pingRequest';
import { UrlFallbackView } from './urlFallbackView';

export interface RequestWithFallbackOptions<T> {
  /** Сборка итогового URL из базы пула и относительного path */
  buildFullUrl?: (base: string, path: string) => string;
  /** HTTP-статус при успешном ответе (по умолчанию 200) */
  getSuccessStatus?: (response: T) => number;
  /** HTTP-статус при ошибке (по умолчанию err.status ?? 0) */
  getErrorStatus?: (error: unknown) => number;
  /** Сообщение, если все хосты в пуле недоступны */
  unavailableMessage?: string;
}

export const joinBaseAndPath = (base: string, path: string): string => {
  const relativePath = path.replace(/^\/+/, '');
  return relativePath ? `${base}/${relativePath}` : base;
};

const defaultGetErrorStatus = (error: unknown): number =>
  (error as { status?: number })?.status ?? 0;

const isFallbackDebugEnabled = (): boolean => {
  try {
    return localStorage.getItem('debugApiFallback') === '1';
  } catch {
    return false;
  }
};

const logFallback = (event: string, payload: Record<string, unknown>): void => {
  if (!isFallbackDebugEnabled()) {
    return;
  }
  console.info('[ApiFallback]', event, payload);
  console.info(
    '[ApiFallbackPlaneJson]',
    stringifyFallbackLog({ event, payload }),
  );
};

const stringifyFallbackLog = (value: unknown): string => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    return JSON.stringify({
      stringifyError: error instanceof Error ? error.message : String(error),
    });
  }
};

let pingRequestPreloaderWatchTimer: number | undefined;

const showPingRequestPreloaderIfNeeded = (
  urlFallback: UrlFallbackView,
): void => {
  if (!shouldWaitForPingRequest(urlFallback)) {
    hidePingRequestPreloader();
    return;
  }

  pingRequestBusy$.next(true);

  // Плашка гаснет, как только появился пригодный snapshot либо истёк grace (см.
  // shouldWaitForPingRequest) — а не по снятию lock, поэтому она больше не висит весь TTL.
  if (pingRequestPreloaderWatchTimer == null) {
    pingRequestPreloaderWatchTimer = window.setInterval(() => {
      if (!shouldWaitForPingRequest(urlFallback)) {
        hidePingRequestPreloader();
      }
    }, 1000);
  }
};

const hidePingRequestPreloader = (): void => {
  if (pingRequestPreloaderWatchTimer != null) {
    window.clearInterval(pingRequestPreloaderWatchTimer);
    pingRequestPreloaderWatchTimer = undefined;
  }

  pingRequestBusy$.next(false);
};

const poolExhaustedError = (
  path: string,
  lastError: unknown,
  message: string,
): unknown => {
  const status = defaultGetErrorStatus(lastError);
  if (status === 0 || status === 504) {
    return lastError;
  }

  return {
    status: 0,
    statusText: message,
    url: path,
  };
};

/**
 * На синхронном пути дожидаться завершения ping-цикла нельзя: сам ping шлёт
 * async XHR на этом же главном потоке, а его колбэки (onload/onerror — именно
 * они публикуют snapshot и снимают lock) не выполнятся, пока стек синхронного
 * кода не опустеет. Активное ожидание (busy-wait или любой другой блокирующий
 * вызов) не даёт дождаться результата раньше времени и гарантированно держит
 * вкладку почти весь controlRequestLockTTLMs (по умолчанию 40с) вместо реального
 * времени ответа ping. Поэтому на синхронном пути не ждём вообще: используем то,
 * что уже сохранено в localStorage с прошлого завершённого цикла (или дефолтный
 * порядок, если пинга ещё не было) — устойчивость к сбоям всё равно
 * обеспечивается обычным перебором пула при реальной ошибке запроса, как и в
 * async-пути.
 */
const logSkippedPingWaitIfNeeded = (
  path: string,
  urlFallback: UrlFallbackView,
): void => {
  if (shouldWaitForPingRequest(urlFallback)) {
    logFallback('sync-skip-ping-wait', { path });
  }
};

/**
 * Выполняет HTTP-запрос с перебором URL из UrlFallback.
 *
 * @param path относительный путь (common-getCity) или пустая строка, если base уже полный URL
 * @param request функция запроса по полному URL
 * @param urlFallback пул URL (api, payment, site и т.д.)
 */
export function requestWithFallback<T>(
  path: string,
  request: (fullUrl: string) => Observable<T>,
  urlFallback: UrlFallbackView,
  options: RequestWithFallbackOptions<T> = {},
): Observable<T> {
  const buildFullUrl = options.buildFullUrl ?? joinBaseAndPath;
  const getSuccessStatus = options.getSuccessStatus ?? (() => 200);
  const getErrorStatus = options.getErrorStatus ?? defaultGetErrorStatus;
  const unavailableMessage =
    options.unavailableMessage ?? 'Все сервисы недоступны';

  return new Observable((subscriber) => {
    let lastError: unknown;
    let innerSub: { unsubscribe(): void } | undefined;
    let attemptNo = 0;
    let pingRequestWaitTimer: number | undefined;
    let isDisposed = false;
    const failedIndexes = new Set<number>();
    // Отметка последнего успеха ping на старте цикла: если за время перебора фоновый ping
    // получит более свежий успех, накопленные failedIndexes устаревают — сбрасываем их и
    // начинаем перебор заново с актуального быстрейшего хоста (см. hasFreshUsablePingSince).
    let pingStampAtCycleStart = urlFallback.getLastPingSuccessAt();

    const waitPingRequestAndRetry = () => {
      hideRequestFallbackPreloader();
      showPingRequestPreloaderIfNeeded(urlFallback);
      pingRequestWaitTimer = window.setTimeout(() => {
        pingRequestWaitTimer = undefined;
        if (!isDisposed) {
          attempt();
        }
      }, 250);
    };

    const attempt = () => {
      if (isDisposed) {
        return;
      }
      if (shouldWaitForPingRequest(urlFallback)) {
        waitPingRequestAndRetry();
        return;
      }
      hidePingRequestPreloader();
      attemptNo++;
      const target = urlFallback.tryGetNearestAvailableUrl(failedIndexes);
      if (!target) {
        hideRequestFallbackPreloader();
        logFallback('exhausted', {
          path,
          attemptNo,
          pool: urlFallback.getUrls(),
          lastStatus: defaultGetErrorStatus(lastError),
          lastUrl: (lastError as { url?: string })?.url ?? null,
        });
        subscriber.error(
          poolExhaustedError(path, lastError, unavailableMessage),
        );
        return;
      }

      const { url: base, index: plannedIndex } = target;
      const fullUrl = buildFullUrl(base, path);
      logFallback('attempt', {
        path,
        attemptNo,
        plannedIndex,
        baseUrl: base,
        fullUrl,
      });

      innerSub = request(fullUrl).subscribe({
        next: (response) => {
          hidePingRequestPreloader();
          hideRequestFallbackPreloader();
          const status = getSuccessStatus(response);
          const isFinal = urlFallback.checkAndRecordResult(
            status,
            plannedIndex,
          );
          logFallback('success', {
            path,
            attemptNo,
            plannedIndex,
            fullUrl,
            status,
            isFinal,
          });
          subscriber.next(response);
          subscriber.complete();
        },
        error: (err) => {
          const isFinalByStatus = urlFallback.checkAndRecordResult(
            getErrorStatus(err),
            plannedIndex,
          );
          if (!isFinalByStatus) {
            failedIndexes.add(plannedIndex);
          }
          // Появился свежий пригодный результат ping (не API-ошибка вроде 401/404) —
          // перебор устаревшего порядка бессмыслен: сбрасываем failedIndexes, сдвигаем
          // отметку (чтобы не перезапускаться повторно на том же ping) и начинаем заново.
          if (
            !isFinalByStatus &&
            urlFallback.hasFreshUsablePingSince(pingStampAtCycleStart)
          ) {
            failedIndexes.clear();
            pingStampAtCycleStart = urlFallback.getLastPingSuccessAt();
            logFallback('restart-on-fresh-ping', { path, attemptNo });
            showRequestFallbackPreloader(
              `Ошибка соединения. Ищем работающий маршрут к серверу ...\n${base}`,
            );
            lastError = err;
            attempt();
            return;
          }
          const isFinal =
            isFinalByStatus ||
            failedIndexes.size >= urlFallback.getUrls().length;
          logFallback(isFinal ? 'error-final' : 'error-retry', {
            path,
            attemptNo,
            plannedIndex,
            fullUrl: (err as { url?: string })?.url ?? fullUrl,
            status: getErrorStatus(err),
            statusText: (err as { statusText?: string })?.statusText ?? null,
            isFinal,
            willRetry: !isFinal,
          });
          if (isFinal) {
            hidePingRequestPreloader();
            hideRequestFallbackPreloader();
            subscriber.error(err);
          } else {
            showRequestFallbackPreloader(
              `Ошибка соединения. Ищем работающий маршрут к серверу ...\n${base}`,
            );
            lastError = err;
            attempt();
          }
        },
      });
    };

    attempt();
    return () => {
      isDisposed = true;
      if (pingRequestWaitTimer != null) {
        window.clearTimeout(pingRequestWaitTimer);
      }
      hideRequestFallbackPreloader();
      hidePingRequestPreloader();
      innerSub?.unsubscribe();
    };
  });
}

export interface SyncRequestResult<T> {
  status: number;
  data: T;
}

export class SyncRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly responseText: string,
  ) {
    super(`HTTP ${status}`);
  }
}

export function requestWithFallbackSync<T>(
  path: string,
  request: (fullUrl: string) => SyncRequestResult<T>,
  urlFallback: UrlFallbackView,
  options: Pick<
    RequestWithFallbackOptions<T>,
    'buildFullUrl' | 'unavailableMessage'
  > = {},
): T {
  const buildFullUrl = options.buildFullUrl ?? joinBaseAndPath;
  let lastError: unknown;
  const failedIndexes = new Set<number>();
  let attemptNo = 0;

  logSkippedPingWaitIfNeeded(path, urlFallback);

  for (;;) {
    attemptNo++;
    const target = urlFallback.tryGetNearestAvailableUrl(failedIndexes);
    if (!target) {
      logFallback('exhausted', {
        path,
        attemptNo,
        pool: urlFallback.getUrls(),
        lastStatus: defaultGetErrorStatus(lastError),
        lastUrl: null,
      });
      throw poolExhaustedError(
        path,
        lastError,
        options.unavailableMessage ?? 'Все сервисы недоступны',
      );
    }

    const { url: base, index: plannedIndex } = target;
    const fullUrl = buildFullUrl(base, path);
    logFallback('attempt', {
      path,
      attemptNo,
      plannedIndex,
      baseUrl: base,
      fullUrl,
    });

    try {
      const { status, data } = request(fullUrl);

      if (status === 200) {
        const isFinal = urlFallback.checkAndRecordResult(status, plannedIndex);
        logFallback('success', {
          path,
          attemptNo,
          plannedIndex,
          fullUrl,
          status,
          isFinal,
        });
        return data;
      }

      const isFinalByStatus = urlFallback.checkAndRecordResult(
        status,
        plannedIndex,
      );
      const err = new SyncRequestError(
        status,
        typeof data === 'string' ? data : '',
      );
      if (!isFinalByStatus) {
        failedIndexes.add(plannedIndex);
      }
      const isFinal =
        isFinalByStatus || failedIndexes.size >= urlFallback.getUrls().length;
      logFallback(isFinal ? 'error-final' : 'error-retry', {
        path,
        attemptNo,
        plannedIndex,
        fullUrl,
        status,
        statusText: err.message,
        isFinal,
        willRetry: !isFinal,
      });

      if (isFinal) {
        throw err;
      }
      lastError = err;
    } catch (err) {
      if (err instanceof SyncRequestError) {
        throw err;
      }
      const status = 0;
      const isFinalByStatus = urlFallback.checkAndRecordResult(
        status,
        plannedIndex,
      );
      if (!isFinalByStatus) {
        failedIndexes.add(plannedIndex);
      }
      const isFinal =
        isFinalByStatus || failedIndexes.size >= urlFallback.getUrls().length;
      logFallback(isFinal ? 'error-final' : 'error-retry', {
        path,
        attemptNo,
        plannedIndex,
        fullUrl,
        status,
        statusText: (err as { message?: string })?.message ?? null,
        isFinal,
        willRetry: !isFinal,
      });
      if (isFinal) {
        throw err;
      }
      lastError = err;
    }
  }
}
